/**
 * SPIN TENET — CFD Field Probe & Diagnostics Redux State Slice (Gate 3.1)
 *
 * Manages:
 * - Active CFD probe state (point coordinates, physical velocity components, magnitude, sample type)
 * - Viewport probe mode toggle (click-to-probe vs navigation)
 * - Result integrity diagnostics summary (point count, min/max/mean magnitude, Ux/Uy/Uz components)
 * - Raw -> Normalized deterministic verification samples
 *
 * ARCHITECTURAL CONTRACT:
 * - Pure durable metadata and single-point interrogation records.
 * - ZERO large numerical arrays or vertex buffers (boundary audit contract).
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  CfdFieldProbe,
  CfdResultDiagnostics,
  CfdSampleVerification
} from '@contracts/index';

export interface ProbeState {
  /** Active interrogation result at probed 3D coordinate. */
  activeProbe: CfdFieldProbe | null;

  /** Whether click-to-probe interaction is currently active in the 3D viewport. */
  isProbeModeActive: boolean;

  /** Active run currently selected for probing. */
  selectedRunId: string | null;

  /** Statistical diagnostics calculated from authoritative result vectors. */
  diagnostics: CfdResultDiagnostics | null;

  /** Representative sample verification records proving 1:1 physical field preservation. */
  verificationSamples: CfdSampleVerification[];
}

const initialState: ProbeState = {
  activeProbe: null,
  isProbeModeActive: false,
  selectedRunId: null,
  diagnostics: null,
  verificationSamples: []
};

export const probeSlice = createSlice({
  name: 'probe',
  initialState,
  reducers: {
    setActiveProbe: (state, action: PayloadAction<CfdFieldProbe | null>) => {
      state.activeProbe = action.payload;
    },
    setProbeModeActive: (state, action: PayloadAction<boolean>) => {
      state.isProbeModeActive = action.payload;
    },
    toggleProbeMode: (state) => {
      state.isProbeModeActive = !state.isProbeModeActive;
    },
    setSelectedRunId: (state, action: PayloadAction<string | null>) => {
      state.selectedRunId = action.payload;
      // Invalidate active probe if targeted run switches
      if (state.activeProbe && action.payload && state.activeProbe.runId !== action.payload) {
        state.activeProbe = null;
      }
    },
    setDiagnostics: (state, action: PayloadAction<CfdResultDiagnostics | null>) => {
      state.diagnostics = action.payload;
    },
    setVerificationSamples: (state, action: PayloadAction<CfdSampleVerification[]>) => {
      state.verificationSamples = action.payload;
    },
    clearProbe: (state) => {
      state.activeProbe = null;
    }
  }
});

export const {
  setActiveProbe,
  setProbeModeActive,
  toggleProbeMode,
  setSelectedRunId,
  setDiagnostics,
  setVerificationSamples,
  clearProbe
} = probeSlice.actions;

export default probeSlice.reducer;
